import { useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
  MarkerType,
} from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import '@xyflow/react/dist/style.css';
import { cn } from '@/shared/lib/utils';

const NODE_WIDTH = 200;
const NODE_LABEL_HEIGHT = 18;
const NODE_PAD_Y = 20;
const NODE_LINE_HEIGHT = 18;
const NODE_CHARS_PER_LINE = 24;

export type KnowledgeFlowEntity = {
  label: string;
  id: string;
  domain?: string;
  locale?: string;
  props?: Record<string, unknown>;
};

export type KnowledgeFlowEdge = {
  type: string;
  from_label: string;
  from_id: string;
  to_label: string;
  to_id: string;
};

export type KnowledgeFlowSelection = {
  label: string;
  id: string;
  name: string;
};

type FlowNodeData = {
  label: string;
  entityId: string;
  name: string;
  domain: string;
  selected: boolean;
};

function entityName(entity: KnowledgeFlowEntity): string {
  const props = entity.props ?? {};
  const name = props.name ?? props.title ?? props.key ?? props.value ?? entity.id;
  return String(name);
}

function entityDomain(entity: KnowledgeFlowEntity): string {
  return String(entity.domain || entity.props?.domain || '');
}

function estimateNodeHeight(name: string): number {
  const lines = Math.max(1, Math.ceil(name.length / NODE_CHARS_PER_LINE));
  return NODE_PAD_Y + NODE_LABEL_HEIGHT + lines * NODE_LINE_HEIGHT;
}

function FlowEntityNode({ data }: NodeProps) {
  const nodeData = data as FlowNodeData;
  return (
    <div
      className={cn(
        'rounded-md border bg-card px-3 py-2 shadow-sm',
        nodeData.selected ? 'border-primary ring-2 ring-primary/30' : 'border-border',
      )}
      style={{ width: NODE_WIDTH }}
    >
      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {nodeData.label}
        {nodeData.domain ? ` · ${nodeData.domain}` : ''}
      </div>
      <div className="text-sm font-medium leading-snug wrap-break-word whitespace-normal">
        {nodeData.name}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
}

const nodeTypes = { knowledge: FlowEntityNode };

function layoutElements(
  entities: KnowledgeFlowEntity[],
  edges: KnowledgeFlowEdge[],
  selectedKey: string | null,
): { nodes: Node[]; edges: Edge[] } {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({ rankdir: 'TB', nodesep: 28, ranksep: 56 });

  const byKey = new Map<string, KnowledgeFlowEntity>();
  const heights = new Map<string, number>();

  for (const entity of entities) {
    const key = `${entity.label}:${entity.id}`;
    const height = estimateNodeHeight(entityName(entity));
    byKey.set(key, entity);
    heights.set(key, height);
    graph.setNode(key, { width: NODE_WIDTH, height });
  }

  const flowEdges: Edge[] = [];
  for (const edge of edges) {
    const fromKey = `${edge.from_label}:${edge.from_id}`;
    const toKey = `${edge.to_label}:${edge.to_id}`;
    if (!byKey.has(fromKey) || !byKey.has(toKey)) continue;
    graph.setEdge(fromKey, toKey);
    flowEdges.push({
      id: `${fromKey}-${edge.type}-${toKey}`,
      source: fromKey,
      target: toKey,
      label: edge.type,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 14,
        height: 14,
        color: 'var(--foreground)',
      },
      style: { strokeWidth: 1.5, stroke: 'var(--foreground)', opacity: 0.55 },
      labelShowBg: true,
      labelBgPadding: [5, 2],
      labelBgBorderRadius: 4,
      labelBgStyle: {
        fill: 'var(--background)',
        fillOpacity: 1,
        stroke: 'var(--border)',
        strokeWidth: 1,
      },
      labelStyle: {
        fontSize: 10,
        fontWeight: 700,
        fill: 'var(--foreground)',
      },
    });
  }

  dagre.layout(graph);

  const nodes: Node[] = [];
  for (const [key, entity] of byKey) {
    const position = graph.node(key);
    const height = heights.get(key) ?? estimateNodeHeight(entityName(entity));
    nodes.push({
      id: key,
      type: 'knowledge',
      position: {
        x: (position?.x ?? 0) - NODE_WIDTH / 2,
        y: (position?.y ?? 0) - height / 2,
      },
      data: {
        label: entity.label,
        entityId: entity.id,
        name: entityName(entity),
        domain: entityDomain(entity),
        selected: key === selectedKey,
      } satisfies FlowNodeData,
    });
  }

  return { nodes, edges: flowEdges };
}

export function KnowledgeFlowCanvas({
  entities,
  edges,
  selected,
  onSelect,
  className,
}: {
  entities: KnowledgeFlowEntity[];
  edges: KnowledgeFlowEdge[];
  selected: KnowledgeFlowSelection | null;
  onSelect: (selection: KnowledgeFlowSelection | null) => void;
  className?: string;
}) {
  const selectedKey = selected ? `${selected.label}:${selected.id}` : null;

  const { nodes, edges: flowEdges } = useMemo(
    () => layoutElements(entities, edges, selectedKey),
    [entities, edges, selectedKey],
  );

  const onNodeClick = useCallback(
    (_: unknown, node: Node) => {
      const data = node.data as FlowNodeData;
      onSelect({ label: data.label, id: data.entityId, name: data.name });
    },
    [onSelect],
  );

  return (
    <div className={cn('h-full min-h-0 w-full overflow-hidden bg-background', className)}>
      <ReactFlow
        nodes={nodes}
        edges={flowEdges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onPaneClick={() => onSelect(null)}
        fitView
        minZoom={0.2}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} />
      </ReactFlow>
    </div>
  );
}
